import { useOrganizationContext } from 'contexts';
import type { Patient } from 'fhir/r4';
import React, { useState } from 'react';
import {
  Button,
  Card,
  Dropdown,
  Header,
  Icon,
  Input,
  Message,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Popup,
} from 'semantic-ui-react';
import type { DropdownProps } from 'semantic-ui-react';
import type { CommunicationHelper } from 'utils';

import CallerCard from '../callerCard';

import { useGetPatient } from './hooks';

type AssignMemberListModalProps = {
  handleAssignMember: (communicationData: CommunicationHelper) => Promise<void>;
  handleCloseModal: () => void;
  handleSelectCallerCard: (selectedPatient: Patient) => void;
  isAssignMemberModalOpen: boolean;
  isSubmitting: boolean;
  orgId: string;
  selectedCallTicket: CommunicationHelper | null;
  /**
   * The member picked in the modal, absent until one is picked.
   *
   * All seven call sites hold this in a `useState` seeded `undefined` and reset to
   * `null`, and this component is already written for that: the OK button reads
   * `disabled={!selectedCallerCard}` and the child `CallerCard` compares with
   * `selectedCallerCard?.id`. `Patient` alone was a claim the callers never met.
   */
  selectedCallerCard: Patient | null | undefined;
  /**
   * `| undefined` explicit, not a narrowing: callers pass an optional value, so the
   * property is present holding `undefined`, which a bare `?` rejects under
   * `exactOptionalPropertyTypes`.
   */
  searchQuery?: string | undefined;
};

const AssignMemberListModal: React.FC<AssignMemberListModalProps> = ({
  handleAssignMember,
  handleCloseModal,
  handleSelectCallerCard,
  isAssignMemberModalOpen,
  isSubmitting,
  orgId: _orgId,
  selectedCallTicket,
  selectedCallerCard,
  searchQuery,
}) => {
  const { payersOptions, selectedPayor, setSelectedPayor } = useOrganizationContext();

  const [searchString, setSearchString] = useState<string>(searchQuery ? searchQuery : '');

  const { isValidating, patientData } = useGetPatient(selectedPayor ? selectedPayor : 'ALL', searchString, true);

  const onSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(event.target.value);
  };
  const handlePayerSelection = (e: React.SyntheticEvent<HTMLElement>, value: DropdownProps) => {
    if (selectedPayor !== value.value) {
      setSelectedPayor(value.value as string);
    }
  };
  return (
    <Modal open={isAssignMemberModalOpen} size="small">
      <ModalHeader>
        Please select the member who called before you proceed.
        <Header>
          <Header.Subheader
            style={{
              marginTop: '3px',
              fontWeight: 'bold',
            }}
          >
            <Popup
              flowing
              trigger={
                <Dropdown
                  defaultValue={payersOptions[0]!.value}
                  pointing
                  style={{ zIndex: '99999' }}
                  className="link item"
                  options={payersOptions}
                  onChange={(e, value) => handlePayerSelection(e, value)}
                />
              }
              content="Payer Organization"
              on="hover"
            />
          </Header.Subheader>
        </Header>
      </ModalHeader>
      <ModalContent>
        <Input
          style={{ width: '100%' }}
          value={searchString}
          icon={<Icon size="large" name="search" color="blue" link />}
          onChange={onSearchInputChange}
          placeholder="Search Members"
          loading={isValidating}
        />
        {!isValidating && !patientData?.entry && <Message warning header="No members found." />}
        <Card.Group
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflowY: 'auto',
            maxHeight: 500,
            padding: '5px',
            gap: 5,
            marginTop: '10px',
          }}
        >
          {!isValidating &&
            patientData?.entry?.map((patient) => {
              const patientResource = patient.resource;

              return (
                <CallerCard
                  key={patientResource.id}
                  handleSelectCallerCard={handleSelectCallerCard}
                  patient={patientResource}
                  selectedCallerCard={selectedCallerCard}
                />
              );
            })}
        </Card.Group>
      </ModalContent>
      <ModalActions>
        <Button color="grey" onClick={handleCloseModal}>
          Cancel
        </Button>
        {/*
          `selectedCallTicket!` — every one of the seven call sites opens this modal
          only from its own `handleAssignClick`, which sets the ticket on the line
          immediately before `setIsAssignMemberModalOpen(true)`, and clears both
          together on close. So a rendered modal always has a ticket; the `| null` in
          the prop type covers the closed state, which never reaches this button.
        */}
        <Button
          color="green"
          disabled={!selectedCallerCard}
          loading={isSubmitting}
          onClick={() => handleAssignMember(selectedCallTicket!)}
        >
          <Icon name="checkmark" /> OK
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default AssignMemberListModal;
